<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl mb-4">
      <h1 class="text-xl font-semibold text-indigo-300 flex items-center gap-2">
        <i class="fas fa-history text-indigo-400"></i>
        ประวัติการลอบสังหาร
      </h1>
      <p class="text-sm text-indigo-200">ดูรายละเอียดการลอบสังหารทั้งหมดของคุณ</p>
    </div>

    <!-- Filters -->
    <div class="mb-4 flex flex-col sm:flex-row gap-3">
      <div class="relative flex-1">
        <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        <input
          v-model="searchQuery"
          placeholder="ค้นหาด้วยชื่อผู้โจมตี, ผู้รอดชีวิต, หรือ UID"
          class="w-full bg-gray-700 text-indigo-200 pl-10 p-2 rounded-md border border-gray-600 focus:outline-none focus:border-indigo-400 transition-all duration-200"
          @input="debouncedSearch"
        />
      </div>
      <select
        v-model="filterType"
        class="bg-gray-700 text-indigo-200 p-2 rounded-md border border-gray-600 focus:outline-none focus:border-indigo-400 transition-all duration-200"
        @change="fetchAssassinationLogs"
      >
        <option value="">ทั้งหมด</option>
        <option value="attacker">โจมตี</option>
        <option value="victim">ถูกโจมตี</option>
      </select>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-4 text-indigo-200">
      <i class="fas fa-spinner animate-spin text-indigo-400 mr-2"></i>
      กำลังโหลดข้อมูล...
    </div>

    <!-- Error Message -->
    <div v-else-if="error" class="text-red-300 text-sm text-center mb-4 flex items-center justify-center gap-2">
      <i class="fas fa-exclamation-triangle text-red-400"></i>
      {{ error }}
    </div>

    <!-- Assassination History Table -->
    <div v-else-if="filteredLogs.length > 0" class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-lg shadow-xl overflow-x-auto">
      <table class="w-full text-sm text-indigo-200 divide-y divide-gray-700">
        <thead class="bg-gray-800">
          <tr>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-calendar-alt text-indigo-400 text-xs"></i>
                วันที่และเวลา
              </div>
            </th>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-user-ninja text-red-400 text-xs"></i>
                ผู้โจมตี
              </div>
            </th>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-user-shield text-green-400 text-xs"></i>
                ผู้รอดชีวิต
              </div>
            </th>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-door-open text-indigo-400 text-xs"></i>
                ห้องที่เลือก
              </div>
            </th>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-dice text-blue-400 text-xs"></i>
                ผลลูกเต๋า
              </div>
            </th>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-heart text-red-400 text-xs"></i>
                เลือดคงเหลือ
              </div>
            </th>
            <th class="p-3 text-left font-semibold text-indigo-300">
              <div class="flex items-center gap-1">
                <i class="fas fa-gift text-yellow-400 text-xs"></i>
                รางวัล/การสูญเสีย
              </div>
            </th>
            <th class="p-3 text-center font-semibold text-indigo-300">
              <div class="flex items-center justify-center gap-1">
                <i class="fas fa-skull text-gray-400 text-xs"></i>
                ผู้รอดชีวิตตาย
              </div>
            </th>
            <th class="p-3 text-center font-semibold text-indigo-300">
              <div class="flex items-center justify-center gap-1">
                <i class="fas fa-skull-crossbones text-red-400 text-xs"></i>
                ผู้โจมตีตาย
              </div>
            </th>
            <th class="p-3 text-left"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-700">
          <tr
            v-for="log in filteredLogs"
            :key="log.id"
            class="hover:bg-gray-700 transition-all duration-200"
          >
            <td class="p-3">{{ formatTimestamp(log.timestamp) }}</td>
            <td class="p-3">{{ log.details.attackerName || 'ไม่ระบุ' }}</td>
            <td class="p-3">{{ log.details.survivorName || 'ไม่ระบุ' }}</td>
            <td class="p-3">{{ log.details.selectedRoom || 'ไม่ระบุ' }}</td>
            <td class="p-3">
              <span v-if="log.details.playerDiceResults?.length || log.details.survivorDiceResults?.length">
                {{ log.details.playerDiceResults?.join(' + ') || 'ไม่มี' }} ({{ log.details.totalPlayerDice || 0 }}) vs
                {{ log.details.survivorDiceResults?.join(' + ') || 'ไม่มี' }} ({{ log.details.totalSurvivorDice || 0 }})
              </span>
              <span v-else>ไม่มี</span>
            </td>
            <td class="p-3">
              <div class="space-y-1">
                <div class="flex items-center gap-1">
                  <i class="fas fa-fist-raised text-red-400 text-xs"></i>
                  <span class="text-red-300">โจมตี: {{ getRemainingHp(log, 'attacker') }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <i class="fas fa-shield-alt text-green-400 text-xs"></i>
                  <span class="text-green-300">รอดชีวิต: {{ getRemainingHp(log, 'survivor') }}</span>
                </div>
              </div>
            </td>
            <td class="p-3">
              <div class="space-y-1 text-xs">
                <div v-if="log.details.rewards?.diamondToPlayer || log.details.rewards?.goldToPlayer" class="text-green-300">
                  <span class="font-semibold">ได้รับ:</span>
                  <div class="ml-2">
                    <div v-if="log.details.rewards.diamondToPlayer">💎 {{ log.details.rewards.diamondToPlayer }}</div>
                    <div v-if="log.details.rewards.goldToPlayer">🧈 {{ log.details.rewards.goldToPlayer }}</div>
                  </div>
                </div>
                <div v-if="log.details.rewards?.diamondLostBySurvivor || log.details.rewards?.goldLostBySurvivor" class="text-red-300">
                  <span class="font-semibold">ผู้รอดเสีย:</span>
                  <div class="ml-2">
                    <div v-if="log.details.rewards.diamondLostBySurvivor">💎 {{ log.details.rewards.diamondLostBySurvivor }}</div>
                    <div v-if="log.details.rewards.goldLostBySurvivor">🧈 {{ log.details.rewards.goldLostBySurvivor }}</div>
                  </div>
                </div>
                <div v-if="log.details.rewards?.rankBonus && (log.details.rewards.rankBonus.diamond > 0 || log.details.rewards.rankBonus.gold > 0)" class="text-yellow-300">
                  <span class="font-semibold">โบนัสอันดับ:</span>
                  <div class="ml-2">
                    <div v-if="log.details.rewards.rankBonus.diamond">💎 {{ log.details.rewards.rankBonus.diamond }}</div>
                    <div v-if="log.details.rewards.rankBonus.gold">🧈 {{ log.details.rewards.rankBonus.gold }}</div>
                  </div>
                </div>
                <div v-if="!hasRewards(log)" class="text-gray-400">
                  ไม่มีรางวัล
                </div>
              </div>
            </td>
            <td class="p-3 text-center">
              <span v-if="log.details.isSurvivorDead" class="inline-flex items-center justify-center w-6 h-6 bg-red-500 rounded-full">
                <i class="fas fa-check text-white text-xs"></i>
              </span>
              <span v-else class="inline-flex items-center justify-center w-6 h-6 bg-gray-600 rounded-full">
                <i class="fas fa-times text-gray-400 text-xs"></i>
              </span>
            </td>
            <td class="p-3 text-center">
              <span v-if="log.details.isPlayerDead" class="inline-flex items-center justify-center w-6 h-6 bg-red-500 rounded-full">
                <i class="fas fa-check text-white text-xs"></i>
              </span>
              <span v-else class="inline-flex items-center justify-center w-6 h-6 bg-gray-600 rounded-full">
                <i class="fas fa-times text-gray-400 text-xs"></i>
              </span>
            </td>
            <td class="p-3 text-right">
              <div class="flex items-center justify-end gap-2">
                <button @click="openModal(log)" class="text-indigo-400 hover:text-indigo-300 transition-colors duration-200" title="ดูรายละเอียด">
                  <i class="fas fa-eye"></i>
                </button>
                <button @click.stop="confirmDelete(log)" class="text-red-400 hover:text-red-300 transition-colors duration-200" title="ลบบันทึก">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- No Data Message -->
    <div v-else class="text-indigo-200 text-sm text-center py-4 flex items-center justify-center gap-2">
      <i class="fas fa-search text-indigo-400"></i>
      ไม่พบประวัติการลอบสังหาร
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore" class="text-center mt-4">
      <button
        @click="loadMore"
        class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-md transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
        :disabled="loadingMore"
      >
        <i class="fas fa-arrow-down text-xs"></i>
        <span v-if="loadingMore">กำลังโหลด...</span>
        <span v-else>โหลดเพิ่ม</span>
      </button>
    </div>

    <!-- Back Button -->
    <div class="text-center mt-4">
      <button
        @click="goToMenu"
        class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-md transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
      >
        <i class="fas fa-arrow-left text-xs"></i>
        กลับไปเมนู
      </button>
    </div>

    <!-- Modal for Detailed View -->
    <div v-if="selectedLog" class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4 transition-opacity duration-300 animate-modalFade">
      <div class="bg-gray-800 rounded-lg p-5 w-full max-w-lg shadow-xl flex flex-col gap-4 border border-indigo-500 animate-scaleIn">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold text-indigo-300 flex items-center gap-2">
            <i class="fas fa-info-circle text-indigo-400"></i>
            รายละเอียดการลอบสังหาร
          </h3>
          <button @click="closeModal" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200">
            <i class="fas fa-times text-base"></i>
          </button>
        </div>
        <div class="overflow-y-auto max-h-96 scrollbar-thin text-indigo-200 text-sm space-y-3">
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-calendar-alt text-indigo-400 mr-1"></i>วันที่และเวลา:</span> {{ formatTimestamp(selectedLog.timestamp) }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-user-ninja text-red-400 mr-1"></i>ผู้โจมตี:</span> {{ selectedLog.details.attackerName || 'ไม่ระบุ' }} (ID: {{ selectedLog.details.attackerId || 'N/A' }})</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-user-shield text-green-400 mr-1"></i>ผู้รอดชีวิต:</span> {{ selectedLog.details.survivorName || 'ไม่ระบุ' }} (ID: {{ selectedLog.details.survivorId || 'N/A' }})</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-trophy text-yellow-400 mr-1"></i>อันดับผู้รอดชีวิต:</span> {{ selectedLog.details.survivorRank || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-door-open text-indigo-400 mr-1"></i>ห้องที่เลือก:</span> {{ selectedLog.details.selectedRoom || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-map-marker-alt text-red-400 mr-1"></i>ห้องจริงก่อนโจมตี:</span> {{ selectedLog.details.actualSurvivorRoom || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-map-marker text-green-400 mr-1"></i>ห้องใหม่หลังโจมตี:</span> {{ selectedLog.details.newSurvivorRoom || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-dice text-blue-400 mr-1"></i>จำนวนลูกเต๋า:</span> {{ selectedLog.details.diceCount || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-dice-d20 text-purple-400 mr-1"></i>ผลลูกเต๋าผู้โจมตี:</span> {{ selectedLog.details.playerDiceResults ? selectedLog.details.playerDiceResults.join(', ') : 'N/A' }} (รวม: {{ selectedLog.details.totalPlayerDice || 'N/A' }})</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-dice-d20 text-cyan-400 mr-1"></i>ผลลูกเต๋าผู้รอดชีวิต:</span> {{ selectedLog.details.survivorDiceResults ? selectedLog.details.survivorDiceResults.join(', ') : 'N/A' }} (รวม: {{ selectedLog.details.totalSurvivorDice || 'N/A' }})</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-eye text-green-400 mr-1"></i>พบกัน:</span> {{ selectedLog.details.isEncountered ? 'ใช่' : 'ไม่' }}</p>
          <div v-if="selectedLog.details.trapData" class="space-y-1">
            <p class="font-bold text-indigo-100 flex items-center gap-2"><i class="fas fa-bolt text-yellow-400"></i>กับดัก:</p>
            <p class="ml-4">ชื่อ: {{ selectedLog.details.trapData.name || 'N/A' }}</p>
            <p class="ml-4">ดาเมจ: {{ selectedLog.details.trapDamage || 'N/A' }}</p>
            <p class="ml-4">จำนวนครั้งเหลือ: {{ selectedLog.details.trapUseCount || 'N/A' }}</p>
          </div>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-fist-raised text-red-400 mr-1"></i>ดาเมจต่อผู้รอดชีวิต:</span> {{ selectedLog.details.damageToSurvivor || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-fist-raised text-orange-400 mr-1"></i>ดาเมจต่อผู้โจมตี:</span> {{ selectedLog.details.damageToPlayer || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-skull text-gray-400 mr-1"></i>ผู้รอดชีวิตตาย:</span> {{ selectedLog.details.isSurvivorDead ? 'ใช่' : 'ไม่' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-skull text-gray-400 mr-1"></i>ผู้โจมตีตาย:</span> {{ selectedLog.details.isPlayerDead ? 'ใช่' : 'ไม่' }}</p>
          <div v-if="selectedLog.details.rewards" class="space-y-1">
            <p class="font-bold text-indigo-100 flex items-center gap-2"><i class="fas fa-gem text-blue-400"></i>รางวัล:</p>
            <pre class="text-xs text-gray-300 overflow-auto bg-gray-700 p-2 rounded-md border border-gray-600">{{ formatRewards(selectedLog.details.rewards) }}</pre>
          </div>
          <div class="space-y-1">
            <p class="font-bold text-indigo-100 flex items-center gap-2"><i class="fas fa-chart-bar text-blue-400"></i>สถิติผู้โจมตี:</p>
            <pre class="text-xs text-gray-300 overflow-auto bg-gray-700 p-2 rounded-md border border-gray-600">{{ formatStats(selectedLog.details.attackerStats) }}</pre>
          </div>
          <div class="space-y-1">
            <p class="font-bold text-indigo-100 flex items-center gap-2"><i class="fas fa-chart-bar text-purple-400"></i>สถิติผู้รอดชีวิต:</p>
            <pre class="text-xs text-gray-300 overflow-auto bg-gray-700 p-2 rounded-md border border-gray-600">{{ formatStats(selectedLog.details.survivorStats) }}</pre>
          </div>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-bolt text-yellow-400 mr-1"></i>พลังงานที่ใช้:</span> {{ selectedLog.details.usedEnergy || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-coins text-yellow-400 mr-1"></i>เหรียญที่ใช้:</span> {{ selectedLog.details.usedSilver || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-fire text-red-400 mr-1"></i>ค่าความโกรธที่ใช้:</span> {{ selectedLog.details.usedFuryLevel || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-money-bill-wave text-green-400 mr-1"></i>ประเภทสกุลเงิน:</span> {{ selectedLog.details.currencyType || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-calendar-day text-indigo-400 mr-1"></i>จำนวนครั้งลอบสังหารวันนี้:</span> {{ selectedLog.details.dailyAssassinationAttempts || 'N/A' }}</p>
          <p class="flex items-center gap-2"><span class="font-bold text-indigo-100"><i class="fas fa-calendar-week text-indigo-400 mr-1"></i>จำนวนครั้งลอบสังหารทั้งหมด:</span> {{ selectedLog.details.totalAssassinationAttempts || 'N/A' }}</p>
        </div>
        <button @click="closeModal" class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 mt-4 flex items-center justify-center gap-2">
          <i class="fas fa-times text-xs"></i>
          ปิด
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300 animate-modalFade">
      <div class="bg-gray-800 rounded-lg p-5 w-full max-w-md shadow-xl flex flex-col gap-4 border border-red-500 animate-scaleIn">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold text-red-300 flex items-center gap-2">
            <i class="fas fa-exclamation-triangle text-red-400"></i>
            ยืนยันการลบ
          </h3>
          <button @click="cancelDelete" class="text-gray-300 hover:text-red-300 transition-colors duration-200">
            <i class="fas fa-times text-base"></i>
          </button>
        </div>
        <div class="text-indigo-200 text-sm space-y-3">
          <p class="text-center text-base">คุณแน่ใจหรือไม่ที่จะลบบันทึกนี้?</p>
          <div class="bg-gray-700 p-3 rounded-md border border-gray-600 space-y-2">
            <p><span class="font-bold text-indigo-100">ผู้โจมตี:</span> {{ logToDelete?.details?.attackerName || 'ไม่ระบุ' }}</p>
            <p><span class="font-bold text-indigo-100">ผู้รอดชีวิต:</span> {{ logToDelete?.details?.survivorName || 'ไม่ระบุ' }}</p>
            <p><span class="font-bold text-indigo-100">เวลา:</span> {{ formatTimestamp(logToDelete?.timestamp) }}</p>
          </div>
          <p class="text-red-300 text-xs text-center italic">การลบนี้ไม่สามารถย้อนกลับได้</p>
        </div>
        <div class="flex gap-3 mt-2">
          <button @click="cancelDelete" class="flex-1 bg-gray-600 hover:bg-gray-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2">
            <i class="fas fa-times text-xs"></i>
            ยกเลิก
          </button>
          <button @click="deleteLog" class="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2" :disabled="deleting">
            <i class="fas fa-trash-alt text-xs"></i>
            <span v-if="deleting">กำลังลบ...</span>
            <span v-else>ลบ</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div v-if="toast.show" class="fixed bottom-6 right-6 z-50 animate-slideIn">
      <div class="rounded-lg p-4 shadow-xl flex items-center gap-3" :class="toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'">
        <i class="fas text-white" :class="toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'"></i>
        <span class="text-white font-medium">{{ toast.message }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import debounce from 'lodash/debounce';

export default {
  name: 'AssassinationHistory',
  setup() {
    const router = useRouter();
    const { user, isLoadingAuth } = getUser();
    const assassinationLogs = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterType = ref('');
    const lastDoc = ref(null);
    const hasMore = ref(true);
    const limit = 20;
    const selectedLog = ref(null);
    const showDeleteModal = ref(false);
    const logToDelete = ref(null);
    const deleting = ref(false);
    const toast = ref({ show: false, message: '', type: '' });

    // Fetch assassination logs
    const fetchAssassinationLogs = async (isLoadMore = false) => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอินเพื่อดูประวัติการลอบสังหาร';
        loading.value = false;
        return;
      }

      const userId = user.value.uid;
      try {
        if (!isLoadMore) {
          loading.value = true;
          assassinationLogs.value = [];
          lastDoc.value = null;
        } else {
          loadingMore.value = true;
        }
        error.value = null;

        let query = projectFirestore
          .collection('logs')
          .where('functionName', '==', 'assassinateSurvivor')
          .orderBy('timestamp', 'desc')
          .limit(limit);

        if (isLoadMore && lastDoc.value) {
          query = query.startAfter(lastDoc.value);
        }

        const snapshot = await query.get();

        if (snapshot.docs.length < limit) {
          hasMore.value = false;
        }

        const newLogs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (isLoadMore) {
          assassinationLogs.value = [...assassinationLogs.value, ...newLogs];
        } else {
          assassinationLogs.value = newLogs;
        }

        lastDoc.value = snapshot.docs[snapshot.docs.length - 1];
      } catch (err) {
        console.error('Error fetching assassination logs:', err.message);
        error.value = 'เกิดข้อผิดพลาดในการโหลดประวัติการลอบสังหาร';
        hasMore.value = false;
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    // Load more logs
    const loadMore = () => {
      if (!loadingMore.value && hasMore.value) {
        fetchAssassinationLogs(true);
      }
    };

    // Debounce search
    const debouncedSearch = debounce(fetchAssassinationLogs, 500);

    // Get remaining HP
    const getRemainingHp = (log, type) => {
      if (!log?.details) return 'ไม่ทราบ';

      const isAttacker = type === 'attacker';
      const maxHp = isAttacker ? log.details.attackerStats?.maxHp : log.details.survivorStats?.maxHp;
      const remaining = isAttacker ? log.details.attackerCurrentHp : log.details.survivorCurrentHp;

      // Older logs may lack server HP; this fight's damage cannot recover prior wounds.
      if (!Number.isFinite(maxHp) || maxHp <= 0 || !Number.isFinite(remaining) || remaining < 0) return 'ไม่ทราบ';

      return `${remaining}/${maxHp}`;
    };

    // Check if log has rewards
    const hasRewards = (log) => {
      const rewards = log.details?.rewards;
      if (!rewards) return false;
      
      return Boolean(
        rewards.diamondToPlayer ||
        rewards.goldToPlayer ||
        rewards.diamondLostBySurvivor ||
        rewards.goldLostBySurvivor ||
        (rewards.rankBonus && (rewards.rankBonus.diamond > 0 || rewards.rankBonus.gold > 0))
      );
    };

    // Filtered and deduplicated logs
    const filteredLogs = computed(() => {
      let logs = assassinationLogs.value;

      // ค้นหาตาม query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        logs = logs.filter(log =>
          log.details.attackerName?.toLowerCase().includes(query) ||
          log.details.survivorName?.toLowerCase().includes(query) ||
          log.details.attackerId === searchQuery.value.trim() ||
          log.details.survivorId === searchQuery.value.trim()
        );
      }

      // กรองตามประเภท
      if (filterType.value) {
        logs = logs.filter(log =>
          filterType.value === 'attacker' ? log.action === 'pvp' && log.uid === log.details.attackerId :
          filterType.value === 'victim' ? log.action === 'survivor' && log.uid === log.details.survivorId :
          true
        );
      }

      // ลบ log ที่ซ้ำกันโดยใช้ unique key
      const seen = new Set();
      return logs.filter(log => {
        const uniqueKey = `${log.details.attackerId}_${log.details.survivorId}_${log.details.selectedRoom}_${log.timestamp?.toMillis?.() || log.timestamp}_${log.details.totalPlayerDice || 0}_${log.details.totalSurvivorDice || 0}`;
        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey);
          return true;
        }
        return false;
      });
    });

    // Format Firestore timestamp
    const formatTimestamp = timestamp => {
      if (!timestamp) return 'ไม่ระบุ';
      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return 'ไม่ระบุ';
      }
    };

    // Format Rewards
    const formatRewards = (rewards) => {
      if (!rewards) return 'N/A';
      return JSON.stringify(rewards, null, 2);
    };

    // Format Stats
    const formatStats = (stats) => {
      if (!stats) return 'N/A';
      return `HP: ${stats.maxHp || 'N/A'}, ATK: ${stats.atk || 'N/A'}, Evasion: ${(stats.evasion * 100).toFixed(2) || 'N/A'}%, Critical: ${(stats.critical * 100).toFixed(2) || 'N/A'}%, Defense: ${stats.defense || 'N/A'}`;
    };

    // Open Modal
    const openModal = (log) => {
      selectedLog.value = log;
    };

    // Close Modal
    const closeModal = () => {
      selectedLog.value = null;
    };

    // Show delete confirmation
    const confirmDelete = (log) => {
      logToDelete.value = log;
      showDeleteModal.value = true;
    };

    // Cancel delete
    const cancelDelete = () => {
      showDeleteModal.value = false;
      logToDelete.value = null;
    };

    // Delete log
    const deleteLog = async () => {
      if (!logToDelete.value) return;

      try {
        deleting.value = true;
        await projectFirestore.collection('logs').doc(logToDelete.value.id).delete();
        
        // Remove from local array
        assassinationLogs.value = assassinationLogs.value.filter(log => log.id !== logToDelete.value.id);
        
        // Show success toast
        showToast('ลบบันทึกสำเร็จ', 'success');
        
        // Close modal
        showDeleteModal.value = false;
        logToDelete.value = null;
      } catch (error) {
        console.error('Error deleting log:', error);
        showToast('เกิดข้อผิดพลาดในการลบบันทึก', 'error');
      } finally {
        deleting.value = false;
      }
    };

    // Show toast notification
    const showToast = (message, type) => {
      toast.value = { show: true, message, type };
      setTimeout(() => {
        toast.value.show = false;
      }, 3000);
    };

    // Navigate to home
    const goToMenu = () => router.push({ name: 'Home' });

    onMounted(() => {
      if (!isLoadingAuth.value && user.value) {
        fetchAssassinationLogs();
      }
    });

    watch([isLoadingAuth, user, filterType], ([newLoadingAuth, newUser]) => {
      if (!newLoadingAuth && newUser) {
        fetchAssassinationLogs();
      } else if (!newUser) {
        assassinationLogs.value = [];
        loading.value = false;
        error.value = 'กรุณาล็อกอินเพื่อดูประวัติการลอบสังหาร';
      }
    });

    return {
      assassinationLogs,
      loading,
      loadingMore,
      error,
      searchQuery,
      filterType,
      filteredLogs,
      formatTimestamp,
      formatRewards,
      formatStats,
      goToMenu,
      debouncedSearch,
      loadMore,
      hasMore,
      selectedLog,
      openModal,
      closeModal,
      getRemainingHp,
      hasRewards,
      showDeleteModal,
      logToDelete,
      confirmDelete,
      cancelDelete,
      deleteLog,
      deleting,
      toast,
    };
  },
};
</script>

<style scoped>
#style-1::-webkit-scrollbar {
  width: 5px;
}
#style-1::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
#style-1 {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}
button:not(:disabled):active {
  transform: scale(0.98);
}
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}
@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

</style>
